import { getIconComponent } from '../components/IconPicker';
import React from 'react';
import { createRoot, Root } from 'react-dom/client';

const generateFaviconWithThemeSupport = (IconComponent: React.ComponentType<{ size: number }>): Promise<string> => {
  return new Promise((resolve, reject) => {
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.width = '32px';
    container.style.height = '32px';
    container.style.color = 'rgb(39, 39, 41)';
    document.body.appendChild(container);

    const root: Root = createRoot(container);
    root.render(React.createElement(IconComponent, { size: 32 }));

    const checkForIcon = (attempts = 0): void => {
      const iconElement = container.querySelector('svg');
      if (iconElement) {
        const viewBox = iconElement.getAttribute('viewBox') || '0 0 24 24';
        iconElement.setAttribute('width', '32');
        iconElement.setAttribute('height', '32');
        iconElement.setAttribute('viewBox', viewBox);

        const existingStyle = iconElement.querySelector('style');
        if (existingStyle) {
          existingStyle.remove();
        }

        const allShapes = iconElement.querySelectorAll('path, circle, rect, polygon, line, polyline, ellipse, g');
        allShapes.forEach(shape => {
          if (shape.hasAttribute('fill') && shape.getAttribute('fill') === 'none') {
            return;
          }
          if (shape.tagName === 'g') {
            const children = shape.querySelectorAll('path, circle, rect, polygon, line, polyline, ellipse');
            children.forEach(child => {
              if (!child.hasAttribute('fill') || child.getAttribute('fill') !== 'none') {
                child.setAttribute('fill', 'currentColor');
              }
            });
          } else {
            shape.setAttribute('fill', 'currentColor');
          }
        });

        const styleElement = document.createElementNS('http://www.w3.org/2000/svg', 'style');
        styleElement.textContent = `
          svg {
            color: rgb(39, 39, 41);
          }
          @media (prefers-color-scheme: dark) {
            svg {
              color: #FFFFFF;
            }
          }
        `;

        iconElement.insertBefore(styleElement, iconElement.firstChild);

        const svgString = new XMLSerializer().serializeToString(iconElement);
        const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);

        setTimeout(() => {
          root.unmount();
          if (container.parentNode) {
            document.body.removeChild(container);
          }
        }, 100);

        resolve(url);
      } else if (attempts < 10) {
        setTimeout(() => checkForIcon(attempts + 1), 50);
      } else {
        root.unmount();
        if (container.parentNode) {
          document.body.removeChild(container);
        }
        reject(new Error('Failed to generate favicon'));
      }
    };

    requestAnimationFrame(() => {
      checkForIcon();
    });
  });
};

const removeAllFaviconLinks = (): void => {
  const existingLinks = document.querySelectorAll("link[rel*='icon']") as NodeListOf<HTMLLinkElement>;
  existingLinks.forEach(link => {
    const oldUrl = link.href;
    if (oldUrl.startsWith('blob:')) {
      URL.revokeObjectURL(oldUrl);
    }
    link.remove();
  });
};

export const updateFavicon = (iconName: string | null): void => {
  if (!iconName) {
    removeAllFaviconLinks();
    const link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/png';
    link.href = '/favicon-2.png';
    document.head.appendChild(link);
    return;
  }

  const IconComponent = getIconComponent(iconName);
  if (!IconComponent) {
    removeAllFaviconLinks();
    const link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/png';
    link.href = '/favicon-2.png';
    document.head.appendChild(link);
    return;
  }

  generateFaviconWithThemeSupport(IconComponent).then((url) => {
    removeAllFaviconLinks();
    const link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/svg+xml';
    link.href = url;
    document.head.appendChild(link);
  }).catch(() => {
    removeAllFaviconLinks();
    const link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/png';
    link.href = '/favicon-2.png';
    document.head.appendChild(link);
  });
};


